import styled, { type DefaultTheme } from 'styled-components';
import { LAYOUT, ORDER } from '@/constants/styles';
import { getFontStyles } from '@/redux/cmsConfig/utils';

/** The brand's card divider colour; `location_card.border_color` is nullable. */
const cardBorderColor = (theme: DefaultTheme) => theme.location_card.border_color ?? 'transparent';

/** The brand's card surface; `location_card.background_color` is nullable. */
const cardSurfaceColor = (theme: DefaultTheme) =>
  theme.location_card.background_color ?? 'transparent';

interface StyledLocationCardProps {
  $isActive?: boolean;
  editFavoriteLocation?: boolean;
  favorites?: boolean;
}

/**
 * Selected and unselected looks come only from existing theme tokens (TEAM-15026): the selected
 * card keeps the brand's card surface with a 2px active-colour border, unselected cards sit on the
 * softer secondary body colour with a transparent border of the same width so selecting a card
 * never shifts the layout.
 *
 * Favorites cards are not a picker (no card is ever selected there) and the account pages paint
 * their content with that same secondary body colour, so they keep the pre-redesign surface and
 * border instead of disappearing into the page.
 */
export const StyledLocationCard = styled.div<StyledLocationCardProps>`
  ${({ $isActive, favorites, theme }) => {
    if (favorites) {
      return `
        background: ${cardSurfaceColor(theme)};
        border: 1px solid ${cardBorderColor(theme)};
      `;
    }

    return `
      background: ${$isActive ? cardSurfaceColor(theme) : theme.global.secondary_body_color};
      border: 2px solid ${$isActive ? theme.global.primary_active_color : 'transparent'};
    `;
  }}
  border-radius: ${({ theme }) => theme.location_card.border_radius}px;
  margin: 0 0 ${LAYOUT.GUTTER}px 0;
  padding: ${LAYOUT.GUTTER}px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.global.primary_active_color};
    outline-offset: 2px;
  }

  // Hover is a desktop affordance for unselected cards in the store finder only.
  ${({ $isActive, favorites, theme }) =>
    !$isActive &&
    !favorites &&
    `
    @media (min-width: 768px) {
      &:hover {
        border-color: ${theme.global.primary_border_color};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      }
    }
  `}

  // Only on Account page
  ${({ editFavoriteLocation }) =>
    editFavoriteLocation &&
    `
    position: relative;
  `}

  // Store Location Card in Account 'Favorites' section
  ${({ favorites }) =>
    favorites &&
    `
    width: calc(50% - ${LAYOUT.GUTTER / 2}px);
  `}

  // TODO - fix
  @media (max-width: 767px) {
    ${({ favorites }) =>
      favorites
        ? `
      width: 100%;
    `
        : `
      z-index: ${ORDER.HEADER};
    `}
  }
`;

export const StyledLocationCardFlex = styled.div`
  align-items: start;
  display: flex;

  @media (max-width: 767px) {
    display: block;
  }
`;

export const StyledCardInfo = styled.div<{ $isActive?: boolean }>(({ theme, $isActive }) => ({
  flex: 2,
  minWidth: 0,
  textAlign: 'left',

  h3: {
    display: 'inline',
    margin: 0,
    ...getFontStyles(theme.location_card.secondary_font, [
      'color',
      'font_family',
      'font_size',
      'font_style',
      'line_height',
    ]),
    // A weight step is one of the non-colour selected signals (AC3).
    fontWeight: $isActive ? 700 : 600,
  },

  p: {
    margin: 0,
    ...getFontStyles(theme.location_card.font),

    b: {
      fontWeight: 'bold',
    },

    a: {
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}));

export const StyledNameRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  text-align: left;
`;

export const StyledPinBadge = styled.span(({ theme }) => ({
  alignItems: 'center',
  border: `1px solid ${theme.global.primary_border_color}`,
  borderRadius: '50%',
  color: theme.global.primary_border_color,
  display: 'inline-flex',
  flexShrink: 0,
  height: '28px',
  justifyContent: 'center',
  width: '28px',
}));

export const StyledSelectedIndicator = styled.span(({ theme }) => ({
  color: theme.global.primary_active_color,
  display: 'inline-flex',
  flexShrink: 0,
}));

export const StyledInfoRow = styled.div(({ theme }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  gap: '8px',
  marginTop: '8px',
  ...getFontStyles(theme.location_card.font),
}));

export const StyledIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  line-height: 0;
  padding-top: 2px;

  svg {
    height: 16px;
    width: 16px;
  }
`;

export const StyledConveyance = styled.div(({ theme }) => ({
  borderTop: `1px solid ${cardBorderColor(theme)}`,
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '12px',
  paddingTop: '12px',
}));

export const StyledConveyanceChip = styled.span(({ theme }) => ({
  alignItems: 'center',
  background: theme.global.secondary_body_color,
  border: `1px solid ${cardBorderColor(theme)}`,
  borderRadius: '4px',
  display: 'inline-flex',
  gap: '6px',
  padding: '4px 8px',
  ...getFontStyles(theme.location_card.font, ['color', 'font_family', 'line_height']),
  fontSize: '0.875rem',

  // The chip centres its icon, so drop the row icon's baseline nudge.
  '> span': {
    paddingTop: 0,
  },
}));

export const StyledVisuallyHidden = styled.span`
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

export const StyledCardLinks = styled.div`
  flex: 1;
  padding-left: ${LAYOUT.GUTTER}px;
  text-align: center;

  @media (max-width: 767px) {
    padding-left: 0;
    display: block;
    margin-top: ${LAYOUT.GUTTER}px;

    a,
    button {
      width: 100%;
    }

    a:last-child {
      margin-bottom: 0;
    }
  }
`;

export const StyledDistance = styled.p`
  display: none;
  font-family: ${({ theme }) => theme.primary_font.medium.font_family};

  @media (max-width: 767px) {
    display: block;

    // Out-specifies the info block's p margin reset so the line clears the chips above it.
    && {
      margin-top: 8px;
    }
  }
`;
